<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'
import {
  Search,
  ChevronDown,
  ChevronRight,
  Save,
  RotateCcw,
  Download,
  Upload,
  Check,
  X,
} from 'lucide-vue-next'
import Card from '@/components/ui/Card.vue'
import Button from '@/components/ui/Button.vue'
import Badge from '@/components/ui/Badge.vue'
import Spinner from '@/components/ui/Spinner.vue'
import { changeGlobalOption, getGlobalOption, type RPCOptions } from '@/api/global'
import { useToast } from '@/composables/useToast'

const { t } = useI18n()
const toast = useToast()
const qc = useQueryClient()

interface OptionMeta {
  key: string
  label: string
  description?: string
  type: 'number' | 'text' | 'bool' | 'select'
  choices?: string[]
  default?: string
}

interface OptionGroup {
  key: string
  label: string
  icon: typeof Download
  options: OptionMeta[]
}

const groups: OptionGroup[] = [
  {
    key: 'download',
    label: 'settings.global.groups.download',
    icon: Download,
    options: [
      { key: 'dir', label: 'settings.global.opt.dir', type: 'text', default: '' },
      { key: 'max-overall-download-limit', label: 'settings.global.opt.max-overall-download-limit', type: 'text', default: '0' },
      { key: 'split', label: 'settings.global.opt.split', type: 'number', default: '4' },
      { key: 'max-connection-per-server', label: 'settings.global.opt.max-connection-per-server', type: 'number', default: '8' },
      { key: 'min-split-size', label: 'settings.global.opt.min-split-size', type: 'text', default: '1M' },
      { key: 'max-tries', label: 'settings.global.opt.max-tries', type: 'number', default: '5' },
      { key: 'timeout', label: 'settings.global.opt.timeout', type: 'number', default: '60' },
      { key: 'http-proxy', label: 'settings.global.opt.http-proxy', type: 'text', default: '' },
    ],
  },
]

const search = ref('')
const expandedGroups = ref<Record<string, boolean>>({
  download: true,
  upload: true,
  bt: false,
  network: false,
  disk: true,
  rpc: false,
  log: false,
  advanced: false,
})

// Local editing state (string values — backend returns/accepts strings)
const localValues = ref<Record<string, string>>({})
const originalValues = ref<Record<string, string>>({})

const optQuery = useQuery({
  queryKey: ['globalOption'],
  queryFn: () => getGlobalOption(),
  refetchOnWindowFocus: false,
})

function applyServerValues(server: RPCOptions): void {
  const next: Record<string, string> = {}
  for (const g of groups) {
    for (const opt of g.options) {
      const raw = server[opt.key]
      next[opt.key] = raw != null ? String(raw) : (opt.default ?? '')
    }
  }
  localValues.value = next
  originalValues.value = { ...next }
}

watch(
  () => optQuery.data.value,
  (val) => {
    if (val) applyServerValues(val)
  },
  { immediate: true },
)

const dirtyKeys = computed(() => {
  const out = new Set<string>()
  for (const k of Object.keys(localValues.value)) {
    if (localValues.value[k] !== (originalValues.value[k] ?? '')) {
      out.add(k)
    }
  }
  return out
})
const dirtyCount = computed(() => dirtyKeys.value.size)

const filteredGroups = computed(() => {
  const q = search.value.trim().toLowerCase()
  if (!q) return groups
  return groups
    .map((g) => ({
      ...g,
      options: g.options.filter((o) => {
        if (o.key.toLowerCase().includes(q)) return true
        // Translated label (current locale) and the raw label key for cross-language search.
        const label = t(o.label).toLowerCase()
        return label.includes(q) || o.label.toLowerCase().includes(q)
      }),
    }))
    .filter((g) => g.options.length > 0)
})

const saveMutation = useMutation({
  mutationFn: (opts: Record<string, string>) => changeGlobalOption(opts),
  onSuccess: (_, vars) => {
    toast.success(t('toast.savedSuccess'))
    // Promote current to baseline so dirty count resets.
    originalValues.value = { ...localValues.value }
    // Also invalidate to pull canonical server state.
    void qc.invalidateQueries({ queryKey: ['globalOption'] })
    void vars
  },
})

let debounceTimer: number | null = null
const autoSave = ref(true)

// v3.15.6: 卸载时清 debounce 定时器 — 离开设置页后残留的 setTimeout 仍会
// 触发 commitDirty() 发配置写入请求 (用户已离开, 写的是过期状态)。
onBeforeUnmount(() => {
  if (debounceTimer !== null) {
    window.clearTimeout(debounceTimer)
    debounceTimer = null
  }
})

function scheduleSave(): void {
  if (!autoSave.value) return
  if (debounceTimer !== null) window.clearTimeout(debounceTimer)
  debounceTimer = window.setTimeout(() => {
    commitDirty()
  }, 500)
}

function commitDirty(): void {
  const payload: Record<string, string> = {}
  for (const k of dirtyKeys.value) {
    payload[k] = localValues.value[k]
  }
  if (Object.keys(payload).length === 0) return
  saveMutation.mutate(payload)
}

function revertAll(): void {
  localValues.value = { ...originalValues.value }
  toast.info(t('settings.global.reverted'))
}

function revertKey(key: string): void {
  if (originalValues.value[key] != null) {
    localValues.value[key] = originalValues.value[key]
  }
}

function toggleGroup(key: string): void {
  expandedGroups.value[key] = !expandedGroups.value[key]
}

function displayValue(key: string, fallback: string): string {
  const v = localValues.value[key]
  return v != null ? v : fallback
}

function onBoolChange(key: string, e: Event): void {
  const checked = (e.target as HTMLInputElement).checked
  localValues.value[key] = checked ? 'true' : 'false'
  scheduleSave()
}

function isBoolChecked(key: string): boolean {
  return /^(true|1|yes)$/i.test(localValues.value[key] ?? '')
}
</script>

<template>
  <div class="space-y-4">
    <!-- Toolbar -->
    <Card padded>
      <div class="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div class="flex-1 max-w-md">
          <div class="relative">
            <Search :size="14" class="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-fg-subtle" />
            <input
              v-model="search"
              type="search"
              :placeholder="t('settings.global.search')"
              class="w-full rounded-md border border-border bg-bg-surface pl-9 pr-3 text-sm text-fg placeholder:text-fg-subtle focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500 h-9"
            />
          </div>
        </div>
        <div class="flex items-center gap-3 text-xs text-fg-muted">
          <label class="inline-flex items-center gap-1.5 cursor-pointer">
            <input
              v-model="autoSave"
              type="checkbox"
              class="rounded border-border"
            />
            <span>{{ t('settings.global.autoSave') }}</span>
          </label>
          <Badge v-if="dirtyCount > 0" variant="warning" dot>
            {{ t('settings.global.dirty', { n: dirtyCount }) }}
          </Badge>
          <Button
            v-if="!autoSave"
            variant="primary"
            size="sm"
            :loading="saveMutation.isPending.value"
            :disabled="dirtyCount === 0"
            @click="commitDirty"
          >
            <template #icon><Save :size="14" /></template>
            {{ t('common.save') }}
          </Button>
          <Button
            v-if="dirtyCount > 0"
            variant="ghost"
            size="sm"
            @click="revertAll"
          >
            <template #icon><RotateCcw :size="14" /></template>
            {{ t('settings.global.revert') }}
          </Button>
        </div>
      </div>
    </Card>

    <!-- Groups -->
    <div v-if="optQuery.isLoading.value" class="flex items-center justify-center py-12">
      <Spinner :size="20" :label="t('common.loading')" />
    </div>

    <div v-else class="space-y-3">
      <section
        v-for="group in filteredGroups"
        :key="group.key"
        class="rounded-lg border border-border bg-bg-surface"
      >
        <button
          type="button"
          class="flex w-full items-center gap-2 px-4 py-2.5 text-left"
          @click="toggleGroup(group.key)"
        >
          <component :is="group.icon" :size="14" class="text-fg-muted" />
          <span class="flex-1 text-sm font-semibold text-fg">{{ t(group.label) }}</span>
          <span class="text-xs text-fg-muted">{{ group.options.length }}</span>
          <ChevronDown v-if="expandedGroups[group.key]" :size="14" class="text-fg-muted" />
          <ChevronRight v-else :size="14" class="text-fg-muted" />
        </button>

        <div v-if="expandedGroups[group.key]" class="border-t border-border">
          <div class="overflow-x-auto">
            <table class="w-full min-w-[480px] text-sm">
              <tbody class="divide-y divide-border">
                <tr v-for="opt in group.options" :key="opt.key" class="align-top">
                  <td class="w-1/2 px-4 py-2.5 md:w-2/5">
                    <div class="flex flex-wrap items-center gap-2">
                      <code class="break-all font-mono text-[12px] text-fg">{{ opt.key }}</code>
                      <Badge v-if="dirtyKeys.has(opt.key)" variant="warning" size="sm">
                        {{ t('settings.global.unsaved') }}
                      </Badge>
                    </div>
                    <div class="mt-0.5 text-xs text-fg-muted">
                      {{ t(opt.label) }}
                    </div>
                  </td>
                  <td class="px-4 py-2.5">
                    <div class="flex items-center gap-2">
                      <!-- Bool -->
                      <label v-if="opt.type === 'bool'" class="inline-flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          class="h-4 w-4 rounded border-border accent-brand-500"
                          :checked="isBoolChecked(opt.key)"
                          @change="onBoolChange(opt.key, $event)"
                        />
                        <span class="text-xs text-fg-muted">
                          {{ isBoolChecked(opt.key) ? 'true' : 'false' }}
                        </span>
                      </label>

                      <!-- Select -->
                      <select
                        v-else-if="opt.type === 'select'"
                        class="w-full max-w-xs rounded-md border border-border bg-bg-surface px-2.5 py-1.5 text-xs text-fg focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                        :value="displayValue(opt.key, opt.default ?? '')"
                        @change="localValues[opt.key] = ($event.target as HTMLSelectElement).value; scheduleSave()"
                      >
                        <option v-for="c in opt.choices ?? []" :key="c" :value="c">{{ c }}</option>
                      </select>

                      <!-- Number -->
                      <input
                        v-else-if="opt.type === 'number'"
                        type="number"
                        class="w-full max-w-xs rounded-md border border-border bg-bg-surface px-2.5 py-1.5 text-xs text-fg focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                        :value="displayValue(opt.key, opt.default ?? '')"
                        @input="localValues[opt.key] = ($event.target as HTMLInputElement).value; scheduleSave()"
                      />

                      <!-- Text -->
                      <input
                        v-else
                        type="text"
                        class="w-full rounded-md border border-border bg-bg-surface px-2.5 py-1.5 text-xs text-fg focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                        :value="displayValue(opt.key, opt.default ?? '')"
                        :placeholder="opt.default ?? ''"
                        @input="localValues[opt.key] = ($event.target as HTMLInputElement).value; scheduleSave()"
                      />

                      <!-- Revert single -->
                      <button
                        v-if="dirtyKeys.has(opt.key)"
                        type="button"
                        class="inline-flex h-6 w-6 items-center justify-center rounded text-fg-muted hover:bg-bg-hover hover:text-fg"
                        :title="t('settings.global.revert')"
                        @click="revertKey(opt.key)"
                      >
                        <RotateCcw :size="12" />
                      </button>
                    </div>
                    <div v-if="opt.default" class="mt-0.5 text-xs text-fg-subtle">
                      {{ t('settings.global.default') }}: <code class="font-mono">{{ opt.default }}</code>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <div v-if="filteredGroups.length === 0" class="rounded-lg border border-border bg-bg-surface p-8 text-center text-sm text-fg-muted">
        {{ t('settings.global.noResults') }}
      </div>
    </div>

    <!-- Floating status footer (saving indicator) -->
    <div v-if="saveMutation.isPending.value" class="fixed bottom-6 right-6 z-30 inline-flex items-center gap-2 rounded-md border border-border bg-bg-surface px-3 py-2 text-xs text-fg-muted shadow-md">
      <Spinner :size="12" />
      {{ t('settings.global.saving') }}
    </div>

    <!-- Icons referenced for build-time tree-shake safety -->
    <span v-if="false" class="hidden"><Check :size="0" /><X :size="0" /></span>
  </div>
</template>

<style scoped>
/* ensure the search input keeps consistent height */
input[type='search'] {
  -webkit-appearance: none;
}
</style>
